import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  FileText, 
  Copy,
  Printer,
  ArrowLeft
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const BETA_AGREEMENT = `
# BETA TESTER AGREEMENT

## All American Dog Club — Beta Testing Program

**Effective Date:** Upon acceptance of beta access

---

## 1. OVERVIEW

Welcome to the All American Dog Club beta testing program ("Beta Program"). By accessing or using the All American Dog Club platform ("Platform") during the beta period, you ("Tester" or "you") agree to be bound by the terms of this Beta Tester Agreement ("Agreement").

The Platform is operated by Very Big Box LLC ("VBB" or "we/us/our") and is provided to beta testers for the purpose of evaluation, feedback, and early community building.

---

## 2. BETA SERVICE — "AS IS"

2.1. The Platform is provided on an **"as-is" and "as-available"** basis during the beta period. Features, functionality, and user interfaces are under active development and may change, be modified, or be removed at any time without prior notice.

2.2. We make **no guarantees** regarding uptime, availability, performance, or fitness for any particular purpose during the beta period.

2.3. **Known limitations** during beta include but are not limited to:
- Maximum of 5 registered pets for unapproved accounts
- Marketplace access requires account approval
- Some features may be incomplete or experimental
- Third-party integrations may be in testing phases

---

## 3. YOUR DATA

3.1. We **intend to retain** all data you provide during the beta period, and we expect to carry your account, pet profiles, posts, and other content forward into the production release.

3.2. However, in the unlikely event of a critical technical issue, **data loss may occur**. We will make every reasonable effort to prevent this, but cannot guarantee complete data preservation during the beta period.

3.3. If you experience any data-related issues or need urgent assistance, contact us immediately at: **support@americandogclub.com**

---

## 4. FEEDBACK AND SUBMISSIONS

4.1. You may be invited to provide feedback, report bugs, suggest features, or otherwise contribute to the development of the Platform. Any feedback you provide may be used by VBB without restriction or compensation to improve the Platform.

4.2. You retain ownership of any original content you post (photos, text, reviews, etc.), but grant VBB a non-exclusive license to display and use such content within the Platform.

---

## 5. CONTENT CONTRIBUTOR PROGRAM

5.1. During the beta period, select testers may be invited to help populate the Platform with information about local resources such as dog parks, veterinary clinics, groomers, trainers, and other service providers ("Contributed Content").

5.2. **All Contributed Content must be either:**
- Your own original work, or
- Properly licensed under an appropriate open license (e.g., Creative Commons, public domain), with attribution provided where required.

5.3. You represent and warrant that any content you submit does not infringe on the intellectual property rights, privacy rights, or any other rights of any third party.

5.4. **Financial Incentives:** VBB may offer financial compensation to content contributors. However, **payment cannot be guaranteed** until and unless the Platform achieves sufficient membership revenue or secures outside funding. Any compensation arrangements will be documented separately in writing.

---

## 6. CONFIDENTIALITY

6.1. The beta access code and certain features of the Platform may be confidential. You agree not to share your beta access credentials with unauthorized individuals.

6.2. You may discuss the Platform publicly in general terms, but should not share screenshots or details of features explicitly marked as confidential or under development.

---

## 7. ACCEPTABLE USE

7.1. You agree to use the Platform in a manner consistent with its purpose as a community for pet owners and enthusiasts.

7.2. You will not:
- Submit false, misleading, or fraudulent information
- Attempt to access other users' accounts or data
- Use the Platform for any illegal purpose
- Harass, abuse, or threaten other users
- Attempt to circumvent security measures

---

## 8. PRIVACY

8.1. Your use of the Platform is subject to our [Privacy Policy](/privacy-policy), which describes how we collect, use, and protect your personal information.

8.2. During the beta period, we may collect additional usage data (such as feature usage patterns and error reports) to improve the Platform. This data is used in aggregate and is not shared with third parties.

---

## 9. TERMINATION

9.1. Either party may terminate participation in the Beta Program at any time.

9.2. VBB reserves the right to revoke beta access for violation of this Agreement or for any reason at its sole discretion.

9.3. Upon termination, we will make reasonable efforts to allow you to export your data, subject to technical feasibility.

---

## 10. LIMITATION OF LIABILITY

10.1. To the maximum extent permitted by law, VBB shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Platform during the beta period.

10.2. Our total liability for any claim arising from the Beta Program shall not exceed the amount you have paid to use the Platform (if any).

---

## 11. MODIFICATIONS

11.1. VBB reserves the right to modify this Agreement at any time. Material changes will be communicated via the Platform or email. Continued use of the Platform after changes constitutes acceptance.

---

## 12. URGENT CONTACT

For urgent matters, data issues, or security concerns during the beta period:

**Email:** support@americandogclub.com

---

## 13. ACCEPTANCE

By entering the beta access code and using the Platform, you acknowledge that you have read, understood, and agree to be bound by the terms of this Beta Tester Agreement.

---

*Last updated: March 2026*
*Very Big Box LLC — All American Dog Club*
`;

const BetaTesterAgreement = () => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(BETA_AGREEMENT);
    setCopied(true);
    toast.success('Agreement copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="beta-agreement-page">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <Link to="/">
              <Button variant="ghost" size="sm" className="mb-4">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FileText className="w-6 h-6 text-primary" />
              Beta Tester Agreement
            </h1>
            <p className="text-gray-600 mt-1">Terms for participation in the beta testing program</p>
          </div>
          <div className="flex gap-2 mt-8">
            <Button variant="outline" size="sm" onClick={handleCopy} data-testid="copy-agreement-btn">
              <Copy className="w-4 h-4 mr-2" />
              {copied ? 'Copied!' : 'Copy'}
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-agreement-btn">
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
          </div>
        </div>

        {/* Agreement Content */}
        <Card>
          <CardContent className="py-8 px-6 md:px-10">
            <div className="prose prose-slate max-w-none prose-headings:font-heading prose-h1:text-2xl prose-h2:text-lg prose-h3:text-base prose-p:text-sm prose-li:text-sm prose-table:text-sm">
              <ReactMarkdown>{BETA_AGREEMENT}</ReactMarkdown>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BetaTesterAgreement;
